"use client";

import { markdownify } from "@/lib/utils/textConverter";
import { useInView } from "react-intersection-observer";
import ExpandableImage from "@/components/ExpandableImage";
import { HighlightSectionProps } from "@/types";
import BulletPoints from "@/partials/BulletPoints";

const HighlightSection = ({
  highlights = [],
  reverse = false,
}: {
  highlights?: HighlightSectionProps[];
  reverse?: boolean;
}) => {
  const { ref: ref1, inView: inView1 } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  return (
    <section ref={ref1}>
      <div className="flex flex-col items-center justify-center">
        {highlights.map((highlight: HighlightSectionProps, index) => {
          // Decide layout direction
          let isReversed: boolean;

          if (!reverse) {
            // normal alternating
            isReversed = index % 2 !== 0;
          } else {
            // flipped alternating
            isReversed = index % 2 === 0;
          }

          return (
            <div
              key={index}
              className={`w-full pt-28 ${
                inView1
                  ? "animate-fade-up animate-duration-[500ms] animate-delay-[400ms] font-secondary"
                  : ""
              }`}
            >
              <div
                className={`flex flex-col lg:flex-row items-center ${
                  isReversed ? "lg:flex-row-reverse mt-10" : "lg:flex-row"
                } md:gap-8 gap-12`}
              >
                {/* Image */}
                <div className="lg:h-[450px] md:h-[500px] h-[250px] sm:h-[350px] xl:w-5/6 w-full relative bottom-20">
                  <h3
                    dangerouslySetInnerHTML={markdownify(highlight.title)}
                    className="mb-4 text-dark-grey font-primary animate-fade animate-duration-[600ms] ease-in"
                  />
                  {highlight.subtitle && (
                    <p
                      dangerouslySetInnerHTML={markdownify(highlight.subtitle)}
                      className="mb-2 text-dark-grey text-lg animate-fade animate-duration-[600ms] ease-in"
                    />
                  )}

                  <ExpandableImage
                    className="rounded-xs shadow-md"
                    src={highlight.image}
                    fill
                    alt={highlight.alt}
                  />
                </div>

                {/* Text */}
                <div className="flex flex-col xl:w-4/5 w-full">
                  <p
                    className="pb-6 text-dark-grey text-lg animate-fade animate-delay-[200ms] ease-in"
                    dangerouslySetInnerHTML={markdownify(highlight.content)}
                  />
                  {highlight.bulletpoints &&
                    highlight.bulletpoints.length > 0 && (
                      <BulletPoints bulletpoints={highlight.bulletpoints} />
                    )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default HighlightSection;
